// src/services/api.js
import axios from 'axios';
import { getAuth } from 'firebase/auth';
import app from '../firebase/firebase';
import { getApiBaseUrl, ENV_INFO } from '../config/api';

// Get API base URL from configuration
const apiBaseUrl = getApiBaseUrl();

// Log the base URL being used (helpful for debugging)
console.log('API Base URL being used:', apiBaseUrl);

// Create an axios instance with the correct base URL
const api = axios.create({
  baseURL: apiBaseUrl,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add a request interceptor to add auth token when available
api.interceptors.request.use(
  async (config) => {
    try {
      // Get current Firebase auth instance
      const auth = getAuth(app);
      const user = auth.currentUser;
      
      if (user) {
        // Get a fresh token
        const token = await user.getIdToken(true);
        config.headers.Authorization = `Bearer ${token}`;
        
        // Log that we added the token (without showing the actual token)
        console.log(`Added auth token to ${config.method.toUpperCase()} request to ${config.url}`);
      } else {
        console.log(`No auth token available for ${config.method.toUpperCase()} request to ${config.url}`);
      }
      
      // Handle FormData requests - remove Content-Type header to let axios set it automatically
      if (config.data instanceof FormData) {
        console.log('FormData detected, removing Content-Type header');
        delete config.headers['Content-Type'];
      }
      
      // For debugging - log all headers but hide the full Authorization header
      const debugHeaders = {...config.headers};
      if (debugHeaders.Authorization) {
        debugHeaders.Authorization = 'Bearer [HIDDEN]';
      }
      console.log('Request headers:', debugHeaders);
      
    } catch (error) {
      console.error('Error getting auth token:', error);
    }
    
    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Add a response interceptor for better error handling
api.interceptors.response.use(
  (response) => {
    // Log successful responses
    console.log(`Response from ${response.config.url}: Status ${response.status}`);
    return response;
  },
  (error) => {
    // Enhanced error logging
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      console.error('Response error:', {
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data,
        url: error.config.url,
        method: error.config.method
      });
    } else if (error.request) {
      // The request was made but no response was received
      console.error('Request error (no response):', {
        request: error.request,
        url: error.config.url,
        method: error.config.method
      });
    } else {
      // Something happened in setting up the request that triggered an Error
      console.error('Error setting up request:', error.message);
    }
    
    return Promise.reject(error);
  }
);

export default api;